import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { authApi } from "../services/api";
import { setUnauthorizedHandler } from "../services/http";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [signedOut, setSignedOut] = useState(false);

  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    authApi
      .me()
      .then(({ user }) => setUser(user))
      .catch(() => setUser(null))
      .finally(() => setChecking(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const { user } = await authApi.login({ email, password });
    setSignedOut(false);
    setUser(user);
  }, []);

  // New accounts aren't signed in until the confirmation link is clicked.
  const register = useCallback(
    (name, email, password, acceptedTerms) => authApi.register({ name, email, password, acceptedTerms: Boolean(acceptedTerms) }),
    []
  );

  const logout = useCallback(async () => {
    await authApi.logout().catch(() => {});
    setSignedOut(true);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, checking, signedOut, login, register, logout }),
    [user, checking, signedOut, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
